import { cn } from "@/lib/utils";
import React from "react";
import { FolderIcon } from "../../atoms/FolderIcon";
import { DocumentCountBadge } from "../../atoms/DocumentCountBadge";
import { FolderActionMenu } from "./FolderActionMenu";

interface RootFolderItemProps {
  /** Root folder name (typically "Files") */
  name: string;
  /** Total document count across all folders */
  documentCount?: number;
  /** Whether drop is currently allowed */
  isDragOver?: boolean;
  /** Whether root is a valid destination for the active move. */
  isDropAvailable?: boolean;
  /** Called when root folder is selected */
  onSelect: () => void;
  /** Drag event handlers */
  onDragOver?: (e: React.DragEvent) => void;
  onDragEnter?: (e: React.DragEvent) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  /** Called when create root folder is selected */
  onCreateFolder?: () => void;
  /** Called when export is selected */
  onExport?: () => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * RootFolderItem - Molecule component
 *
 * Special "Files" root folder with drag/drop target support.
 * Composed from FolderIcon and DocumentCountBadge atoms.
 * Always displays as open (isOpen=true) and acts as drag/drop target.
 * Part of the atomic design system for folder tree display.
 *
 * @example
 * ```tsx
 * <RootFolderItem
 *   name="Files"
 *   documentCount={42}
 *   onSelect={handleSelect}
 *   onDragOver={handleDragOver}
 *   onDrop={handleDrop}
 * />
 * ```
 */
export const RootFolderItem = React.memo(function RootFolderItem({
  name,
  documentCount = 0,
  isDragOver = false,
  isDropAvailable = false,
  onSelect,
  onDragOver,
  onDragEnter,
  onDragLeave,
  onDrop,
  onCreateFolder,
  onExport,
  className,
}: RootFolderItemProps) {
  return (
    <div
      className={cn(
        "group flex cursor-pointer items-center rounded-md py-2",
        "select-none transition-all duration-200 ease-out",
        "hover:bg-slate-50 hover:shadow-folder-hover",
        "border-b border-slate-100",
        isDropAvailable && "bg-blue-50/40 ring-1 ring-inset ring-blue-200",
        isDragOver && "bg-blue-50 shadow-folder-drag ring-2 ring-blue-200",
        className
      )}
      onDragOver={onDragOver}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
      aria-label={`${name} folder${documentCount > 0 ? `, ${documentCount} documents` : ""}`}
      data-drop-available={isDropAvailable || undefined}
    >
      {/* Left section */}
      <div className="flex min-w-0 flex-1 items-center gap-2 pl-2 pr-2" onClick={onSelect}>
        <FolderIcon isOpen={true} className="flex-shrink-0" />
        <span className="flex-1 text-sm font-semibold text-slate-800">{name}</span>
      </div>

      {/* Right section - aligned to right edge */}
      <div className="flex flex-shrink-0 items-center gap-2 pr-2">
        {documentCount > 0 && <DocumentCountBadge count={documentCount} />}

        <div className="opacity-0 transition-opacity duration-200 group-hover:opacity-100">
          <FolderActionMenu onCreateSubfolder={onCreateFolder} onExport={onExport} />
        </div>
      </div>
    </div>
  );
});
